import React, { useState, useEffect } from 'react';
import { Card, Row, Col, List, Avatar, Tag, Typography, Statistic, Progress, Space, Spin, Alert, Button } from 'antd';
import { 
  UserOutlined, 
  ProjectOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  TeamOutlined,
  TrophyOutlined,
  CalendarOutlined,
  BarChartOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';
import apiService from '../services/apiService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const TeamOverview = () => {
  const { user } = useAuthStore();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTeamData();
  }, [user]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-manager/dashboard/${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch team data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  const { teamPerformance, projectStats, summary } = dashboardData || {};
  
  // Calculate team insights
  const totalTeamHours = teamPerformance?.reduce((sum, member) => sum + member.totalHours, 0) || 0;
  const avgHoursPerMember = teamPerformance?.length > 0 ? (totalTeamHours / teamPerformance.length).toFixed(1) : 0;
  const mostActiveEmployee = teamPerformance?.reduce((prev, current) => 
    (prev.totalHours > current.totalHours) ? prev : current, teamPerformance[0]
  );
  const activeProjects = projectStats?.filter(project => 
    dayjs().diff(dayjs(project.lastActivity), 'days') <= 7
  ).length || 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <TeamOutlined className="mr-2 text-purple-600" />
          Team Overview
        </Title>
      </div>

      {/* Team Insights */}
      {mostActiveEmployee && (
        <Card title="Team Insights" className="shadow-sm mb-6">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                <TrophyOutlined className="text-2xl text-green-600" />
                <div>
                  <div className="font-medium text-green-800">Top Performer</div>
                  <div className="text-lg font-bold text-green-900">{mostActiveEmployee.employeeName}</div>
                  <div className="text-sm text-green-700">{mostActiveEmployee.totalHours} hours logged</div>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                <ProjectOutlined className="text-2xl text-blue-600" />
                <div>
                  <div className="font-medium text-blue-800">Active Projects</div>
                  <div className="text-lg font-bold text-blue-900">{activeProjects} of {summary?.totalProjects || 0}</div>
                  <div className="text-sm text-blue-700">Projects with recent activity</div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[24, 24]}>
        {/* Main Content - 70% */}
        <Col xs={24} lg={17}>
          <div className="space-y-6">
            {/* Team Members List */}
            <Card title="Team Performance" className="shadow-sm">
              {teamPerformance && teamPerformance.length > 0 ? (
                <div className="grid gap-4">
                  {(Array.isArray(teamPerformance) ? teamPerformance : []).map((member, index) => {
                    const maxHours = Math.max(...teamPerformance.map(t => t.totalHours));
                    const percentage = maxHours > 0 ? Math.round((member.totalHours / maxHours) * 100) : 0;
                    const daysAgo = member.lastActivity ? dayjs().diff(dayjs(member.lastActivity), 'day') : null;
                    const activityColor = daysAgo === null ? 'gray' : daysAgo <= 1 ? 'green' : daysAgo <= 7 ? 'orange' : 'red';
                    const avgHours = member.totalEntries > 0 ? (member.totalHours / member.totalEntries).toFixed(1) : 0;
                    
                    return (
                      <div key={member.employeeId || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                        {/* Top Row - Name and Role */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Avatar size={40} icon={<UserOutlined />} className="bg-blue-500" />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{member.employeeName}</span>
                                <Tag color="blue" className="text-xs px-2 py-0">EMPLOYEE</Tag>
                              </div>
                              <div className="text-sm text-gray-500">{member.email || `${member.employeeName.toLowerCase().replace(' ', '.')}.@qodequay.com`}</div>
                            </div>
                          </div>
                        </div>

                        {/* Bottom Row - Metrics */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <div className="text-xs text-gray-500 mb-1">Allocation</div>
                              <div className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                                {percentage}%
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-500 mb-1">Hours Contributed</div>
                              <div className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm font-medium">
                                {member.totalHours} hours
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-500 mb-1">Projects</div>
                              <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                                {member.projects?.length || 0} projects
                              </div>
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">Compliance</div>
                            <div className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">
                              {percentage >= 80 ? '100%' : percentage >= 60 ? '75%' : '50%'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <TeamOutlined className="text-6xl text-gray-400 mb-4" />
                  <div className="text-xl font-medium text-gray-600 mb-2">No team data available</div>
                  <div className="text-gray-500">Team performance data will appear here once members start logging hours.</div>
                </div>
              )}
            </Card>

            {/* Project Team Statistics */}
            <Card title="Project Statistics" className="shadow-sm">
              {projectStats && projectStats.length > 0 ? (
                <div className="space-y-4">
                  {projectStats.map((project, index) => {
                    const maxProjectHours = Math.max(...projectStats.map(p => p.totalHours));
                    const projectPercentage = maxProjectHours > 0 ? Math.round((project.totalHours / maxProjectHours) * 100) : 0;
                    const avgHoursPerEntry = project.totalEntries > 0 ? (project.totalHours / project.totalEntries).toFixed(1) : 0;
                    
                    return (
                      <div key={project.projectId} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <Text strong className="text-lg">{project.projectName}</Text>
                              {index === 0 && <Tag color="blue" icon={<BarChartOutlined />}>MOST ACTIVE</Tag>}
                            </div>
                            <div className="flex items-center space-x-6 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <TeamOutlined className="text-purple-500" />
                                <span>{project.uniqueEmployees} team members</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <ClockCircleOutlined className="text-blue-500" />
                                <span>{project.totalHours}h total</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <CalendarOutlined className="text-green-500" />
                                <span>{avgHoursPerEntry}h avg/entry</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Tag color="blue">{project.totalEntries} entries</Tag>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <Text className="text-sm">Project Activity</Text>
                              <Text strong className="text-sm">{projectPercentage}%</Text>
                            </div>
                            <Progress 
                              percent={projectPercentage} 
                              size="small"
                              showInfo={false}
                            />
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            Last: {project.lastActivity ? dayjs(project.lastActivity).format('MMM DD') : 'No activity'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ProjectOutlined className="text-4xl text-gray-400 mb-2" />
                  <div className="text-gray-600">No project statistics available</div>
                </div>
              )}
            </Card>
          </div>
        </Col>

        {/* Sidebar - 30% */}
        <Col xs={24} lg={7}>
          <div className="space-y-6">
            {/* Team Summary */}
            <Card title="Team Summary" className="shadow-sm">
              <div className="space-y-4">
                <Statistic
                  title="Team Members"
                  value={summary?.totalEmployees || 0}
                  prefix={<TeamOutlined className="text-purple-500" />}
                />
                <Statistic
                  title="Total Team Hours"
                  value={totalTeamHours}
                  suffix="h"
                  prefix={<ClockCircleOutlined className="text-blue-500" />}
                />
                <Statistic
                  title="Avg Hours/Member"
                  value={avgHoursPerMember}
                  suffix="h"
                  prefix={<CalendarOutlined className="text-green-500" />}
                />
                <Statistic
                  title="Active Projects"
                  value={activeProjects}
                  prefix={<ProjectOutlined className="text-orange-500" />}
                />
              </div>
            </Card>

            {/* Top Performers */}
            <Card title="Top Performers" className="shadow-sm">
              <div className="space-y-3">
                {teamPerformance?.slice(0, 5).map((member, index) => (
                  <div key={member.employeeId} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                        {index + 1}
                      </div>
                      <Text className="text-sm">{member.employeeName}</Text>
                    </div>
                    <Text strong className="text-sm">{member.totalHours}h</Text>
                  </div>
                ))}
                {(!teamPerformance || teamPerformance.length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <div className="text-sm">No performance data</div>
                  </div>
                )}
              </div>
            </Card>

            {/* Project Activity */}
            <Card title="Project Activity" className="shadow-sm">
              <div className="space-y-3">
                {projectStats?.slice(0, 4).map((project) => (
                  <div key={project.projectId}>
                    <div className="flex justify-between items-center mb-1">
                      <Text strong className="text-sm">{project.projectName}</Text>
                      <Text className="text-sm">{project.totalHours}h</Text>
                    </div>
                    <Progress 
                      percent={Math.min((project.totalHours / (projectStats[0]?.totalHours || 1)) * 100, 100)} 
                      size="small"
                      showInfo={false}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{project.uniqueEmployees} members</span>
                      <span>{project.totalEntries} entries</span>
                    </div>
                  </div>
                ))}
                {(!projectStats || projectStats.length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <div className="text-sm">No project data</div>
                  </div>
                )}
              </div>
            </Card>

            {/* Team Insights */}
            <Card title="Team Insights" className="shadow-sm">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Text>Most Productive</Text>
                  <Text strong className="text-sm">
                    {mostActiveEmployee?.employeeName || 'None'}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Team Efficiency</Text>
                  <Text strong>
                    {teamPerformance?.length > 0 ? 
                      `${((teamPerformance.filter(m => m.totalHours > avgHoursPerMember).length / teamPerformance.length) * 100).toFixed(0)}%` : 
                      '0%'
                    }
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Active This Week</Text>
                  <Text strong>
                    {teamPerformance?.filter(member => 
                      member.lastActivity && dayjs().diff(dayjs(member.lastActivity), 'days') <= 7
                    ).length || 0}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Avg Entries/Member</Text>
                  <Text strong>
                    {teamPerformance?.length > 0 ? 
                      (teamPerformance.reduce((sum, member) => sum + member.totalEntries, 0) / teamPerformance.length).toFixed(1) : 
                      '0'
                    }
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default TeamOverview;
